// pages/PolicyDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileText, Printer, Copy } from "lucide-react";

const API_BASE_URL = "https://backend.dlapakistan.com/api/ourpolicies";

const PolicyDetail = () => {
  const { policySlug } = useParams();
  const navigate = useNavigate();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPolicy();
  }, [policySlug]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_BASE_URL}/${policySlug}/`);
      setPolicy(res.data);
    } catch (err) {
      console.error("Error loading policy:", err);
      setError("Unable to load policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const content = `${policy?.heading || ''}\n\n${policy?.content || ''}`;
    navigator.clipboard.writeText(content);
    alert("Policy content copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-6 text-gray-600 font-medium">Loading policy details...</p>
        </div>
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <FileText size={48} className="mx-auto mb-6 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-700 mb-4">
            {error || "Policy not found"}
          </h3>
          <button
            onClick={() => navigate("/about/our-policies")}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition"
          >
            Back to Policies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50" style={{ width: '100vw', maxWidth: '100vw' }}>
      {/* Sticky Header - Full Width */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm" 
        style={{ width: '100vw', left: 0, right: 0 }}>
        <div className="w-screen px-6 lg:px-12 py-4" style={{ width: '100vw' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/about/our-policies")}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft size={20} className="text-gray-700" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 line-clamp-1">
                  {policy.title || policy.heading}
                </h1>
                <p className="text-sm text-gray-500">Policy Document</p>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={copyToClipboard}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Copy to clipboard"
              >
                <Copy size={18} className="text-gray-600" />
              </button>
              <button
                onClick={() => window.print()}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Print"
              >
                <Printer size={18} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Increased Width */}
      <main className="pt-12 pb-16" style={{ width: '100vw', maxWidth: '100vw' }}>
        <div className="w-[98vw] mx-auto px-6 lg:px-12" style={{ width: '98vw', maxWidth: '98vw' }}>
          <article className="overflow-hidden" style={{ width: '100%' }}>
            {/* Header Section - WITHOUT dates */}
            <div className="px-8 pt-10 pb-8 border-b border-gray-100" style={{ width: '100%' }}>
              <div className="flex items-start gap-6">
                <div className="p-4 bg-emerald-50 rounded-xl shrink-0">
                  <FileText size={32} className="text-emerald-600" />
                </div>
                <div className="w-full">
                  <h2 className="text-3xl font-bold text-gray-900">
                    {policy.heading || policy.title}
                  </h2>
                  {/* Removed the dates section completely */}
                </div>
              </div>
            </div>

            {/* Content - Direct HTML Display with increased width */}
            <div className="px-10 py-12" style={{ width: '100%' }}>
              {policy.content?.trim() ? (
                <div 
                  className="policy-content-view"
                  dangerouslySetInnerHTML={{ __html: policy.content }}
                  style={{
                    color: '#374151',
                    fontSize: '18px',
                    lineHeight: '1.8',
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    width: '100%',
                    maxWidth: '100%'
                  }}
                />
              ) : (
                <div className="text-center py-24">
                  <FileText size={64} className="mx-auto mb-8 text-gray-300" />
                  <h3 className="text-2xl font-semibold text-gray-700">No content available yet</h3>
                  <p className="text-gray-500 mt-3">This policy document is currently empty.</p>
                </div>
              )}
            </div>

            {/* Footer - Wider without Document ID */}
            <div className="px-10 py-8 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-4 text-base text-gray-600" style={{ width: '100%' }}>
              <button
                onClick={copyToClipboard}
                className="px-6 py-2.5 rounded-lg hover:bg-white border border-gray-200 hover:border-gray-300 transition flex items-center gap-2 text-gray-700"
              >
                <Copy size={18} />
                Copy Content
              </button>
              <button
                onClick={() => navigate("/about/our-policies")}
                className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition"
              >
                Back to All Policies
              </button>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default PolicyDetail;